import { Form, Formik, FormikProps } from 'formik';
import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Link, LinkProps } from 'react-router-dom';
import * as Yup from 'yup';

import { Box, Button, Grid, Theme, Typography, WithStyles, withStyles } from '@material-ui/core';
import { Omit } from '@material-ui/types';

import { PasswordReset } from '../../models/PasswordReset';
import { TenantInformation } from '../../models/TenantInformation';
import TenantLogo from '../tenant/TenantLogo';
import FormikTextField from '../utils/forms/FormikTextField';

const styles = (theme: Theme) => ({
    root: {
        padding: theme.spacing(3),
        borderColor: theme.palette.grey[300],
        borderRadius: theme.shape.borderRadius,
        borderWidth: 1,
        borderStyle: 'solid'
    },
    title: {
        textAlign: 'center' as const
    },
    logoContainer: {
        textAlign: 'center' as const,
        marginBottom: theme.spacing(3),
    },
    subtitle: {
        textAlign: 'center' as const,
        marginBottom: theme.spacing(3)
    },
    actionRow: {
        marginTop: theme.spacing(2)
    },
    passwordField: {
        flexGrow: 1,
        '& + &': {
            marginLeft: theme.spacing(2)
        }
    },
});

interface ComponentProps {
    tenantInformation: TenantInformation;

    onPasswordReset(passwordReset: PasswordReset): void;
}

type Props = ComponentProps & WithStyles<typeof styles> & WithTranslation;

class PasswordResetForm extends Component<Props> {
    public render() {
        const { tenantInformation, classes, t } = this.props;
        const initialValues: PasswordReset = {
            token: this.getToken(),
            password: '',
        };
        return (
            <Grid container direction="column" className={classes.root}>
                <div className={classes.logoContainer}>
                    <TenantLogo tenantInformation={tenantInformation} />
                </div>
                <Typography variant="h5" component="h1" className={classes.title}>
                    {t('components.login.PasswordResetForm.title')}
                </Typography>
                <Typography className={classes.subtitle}>
                    {t('components.login.common.subtitle', { tenantName: tenantInformation.name })}
                </Typography>
                <Formik
                    component={PasswordResetInnerForm}
                    onSubmit={this.onSubmit}
                    initialValues={initialValues}
                    validationSchema={this.schema()} />
            </Grid>
        );
    }

    private getToken(): string {
        const url = new URL(window.location.toString());
        const token = url.searchParams.get('token');
        return token ? token : '';
    }

    private onSubmit = (formData: PasswordReset) => {
        this.props.onPasswordReset(formData);
    }

    private schema = () => {
        const { t } = this.props;
        return Yup.object().shape({
            password: Yup
                .string()
                .required(t('validation.password.required'))
                .min(8, t('validation.password.min'))
                .matches(
                    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
                    t('validation.password.matches')
                ),
            passwordConfirm: Yup
                .string()
                .required(t('validation.passwordConfirm.required'))
                .oneOf([Yup.ref('password'), null], t('validation.passwordConfirm.invalid')),
        });
    }
}

type ConfirmPasswordResetFormProps = WithStyles<typeof styles> & FormikProps<PasswordReset> & WithTranslation;
const PasswordResetInnerForm = withStyles(styles)(withTranslation()((props: ConfirmPasswordResetFormProps) => {
    const { classes, t } = props;
    const loginLink = React.forwardRef<HTMLAnchorElement, Omit<LinkProps, 'innerRef' | 'to'>>(
        (linkProps: any, ref: any) => <Link innerRef={ref} to="/login" {...linkProps} />,
    );
    return (
        <Form>
            <Grid container direction="column">
                <Box display="flex" flexDirection="row">
                    <FormikTextField name="password" type="password"
                        label={t('components.login.common.password')} margin="normal" variant="outlined"
                        className={classes.passwordField} />
                    <FormikTextField name="passwordConfirm" type="password"
                        label={t('components.login.common.passwordConfirm')} margin="normal" variant="outlined"
                        className={classes.passwordField} />
                </Box>
                <Box display="flex" flexDirection="row" justifyContent="space-between" className={classes.actionRow}>
                    <Button component={loginLink} color="primary">
                        {t('components.login.common.backToLogin')}
                    </Button>
                    <Button variant="contained" color="primary" type="submit" className="right">
                        {t('components.login.PasswordResetForm.submit')}
                    </Button>
                </Box>
            </Grid>
        </Form>
    );
}));

export default withStyles(styles)(withTranslation()(PasswordResetForm));
